import { faBox } from '@fortawesome/pro-regular-svg-icons';
import { faClose, faSearch } from '@fortawesome/pro-solid-svg-icons';
import Stack from '@mui/system/Stack';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { InputAdornment } from 'src/components/InputAdornment';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { Group } from 'src/models/group/_types';
import { Package } from 'src/models/package/_types';
import { Product } from 'src/models/product/_types';
import { WorkoutPlan } from 'src/models/workout-plan/_types';
import { titleize } from 'src/utils/stringFormatter';

import { PosProductCard } from './PosProductCard';

interface FinderCardDialogProps {
  open: boolean;
  onClose: () => void;
  items: Product[] | Package[] | WorkoutPlan[] | Group[];
  itemType: string;
  title: string;
  description: string;
}

export default function FinderCardDialog({
  open,
  onClose,
  items,
  itemType,
  title,
  description,
}: FinderCardDialogProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.finderCardDialog',
  });

  const handleClose = () => {
    onClose();
    setSearch('');
  };

  const [search, setSearch] = useState('');

  const filteredItems = useMemo(() => {
    if (items instanceof Array) {
      return items.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase())
      );
    }
    return [];
  }, [items, search]);

  const addToCartCallback = () => {
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={titleize(title)}
      subtitle={description}
      maxWidth="lg"
    >
      <Grid container spacing={2}>
        <TextField
          placeholder="Search"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon={faSearch} sx={{ cursor: 'pointer' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button onClick={() => setSearch('')} variant="borderless">
                  <Icon icon={faClose} sx={{ cursor: 'pointer' }} />
                </Button>
              </InputAdornment>
            ),
          }}
        />

        {filteredItems.length ? (
          filteredItems.map((item) => (
            <PosProductCard
              key={item?.id}
              itemType={itemType}
              itemId={item.id}
              addToCartCallback={addToCartCallback}
            />
          ))
        ) : (
          <Grid
            container
            spacing={2}
            flex={1}
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            bgcolor="background.level1"
            borderRadius={2}
          >
            <Stack spacing={2} alignItems="center">
              <Icon icon={faBox} size="large" />

              <Stack alignItems="center">
                <Typography variant="h6" color="text.primary" fontWeight={600}>
                  {t('noProducts')}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  textAlign="center"
                  maxWidth={320}
                >
                  {t('noProductsDescription')}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        )}
      </Grid>
    </Dialog>
  );
}
